import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { DocumentUploadResponse, PendingDocumentRecord, UploadedDocumentResult } from 'src/types';
import { environment } from 'src/environments/environment';

const DOCUMENTS_DB_NAME = 'siglar-documents';
const DOCUMENTS_STORE_NAME = 'pending-documents';
const DOCUMENTS_DB_VERSION = 1;

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private readonly uploadInFlight = new Map<string, Observable<UploadedDocumentResult>>();

  constructor(private http: HttpClient) {}

  queueDocument(record: PendingDocumentRecord): Promise<void> {
    return this.withStore('readwrite', (store) => this.wrapRequest(store.put(record)).then(() => undefined));
  }

  getPendingDocument(documentId: string): Promise<PendingDocumentRecord | null> {
    return this.withStore('readonly', (store) => this.wrapRequest<PendingDocumentRecord | undefined>(store.get(documentId)))
      .then((record) => record || null);
  }

  removePendingDocument(documentId: string): Promise<void> {
    return this.withStore('readwrite', (store) => this.wrapRequest(store.delete(documentId)).then(() => undefined));
  }

  uploadPendingDocument(documentId: string, shipUuid: string): Observable<UploadedDocumentResult> {
    const existingUpload = this.uploadInFlight.get(documentId);
    if (existingUpload) {
      return existingUpload;
    }

    const upload$ = from(this.getPendingDocument(documentId)).pipe(
      switchMap((record) => {
        if (!record) {
          return throwError(() => new Error(`Pending document ${documentId} not found`));
        }

        return this.uploadDocument(shipUuid, record).pipe(
          switchMap(({ uuid }) =>
            this.parseDocument(uuid, record).pipe(
              map((parseResult) => ({
                documentUuid: uuid,
                parseResult,
              }))
            )
          )
        );
      }),
      shareReplay(1),
      finalize(() => {
        this.uploadInFlight.delete(documentId);
      })
    );

    this.uploadInFlight.set(documentId, upload$);
    return upload$;
  }

  private uploadDocument(shipUuid: string, record: PendingDocumentRecord) {
    const formData = new FormData();
    formData.append('document', record.file);
    formData.append('type', record.type);
    formData.append('subtype', record.subtype);

    const url = `${environment.siglarApi.url}/api/v1/document/${shipUuid}`;
    return this.http.post<DocumentUploadResponse>(url, formData);
  }

  private parseDocument(documentUuid: string, record: PendingDocumentRecord): Observable<unknown> {
    const parseUrl = this.getParseUrl(documentUuid, record);
    if (!parseUrl) {
      return of(null);
    }

    return this.http.get<unknown>(parseUrl).pipe(catchError(() => of(null)));
  }

  private getParseUrl(documentUuid: string, record: PendingDocumentRecord): string | null {
    if (record.type !== 'bunker') {
      return null;
    }

    const parseSubtype = this.getParseSubtype(record.subtype);
    if (!parseSubtype) {
      return null;
    }

    return `${environment.siglarApi.url}/api/v1/document/parse/${documentUuid}/${parseSubtype}`;
  }

  private getParseSubtype(documentSubtype: string): string | null {
    if (documentSubtype === 'biofuel') {
      return 'pos';
    }

    if (documentSubtype === 'bdn-sampling') {
      return documentSubtype;
    }

    return null;
  }

  private async withStore<T>(
    mode: IDBTransactionMode,
    callback: (store: IDBObjectStore) => Promise<T>
  ): Promise<T> {
    const database = await this.openDatabase();

    try {
      const transaction = database.transaction(DOCUMENTS_STORE_NAME, mode);
      const store = transaction.objectStore(DOCUMENTS_STORE_NAME);
      const result = await callback(store);
      await this.waitForTransaction(transaction);
      return result;
    } finally {
      database.close();
    }
  }

  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = window.indexedDB.open(DOCUMENTS_DB_NAME, DOCUMENTS_DB_VERSION);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(DOCUMENTS_STORE_NAME)) {
          database.createObjectStore(DOCUMENTS_STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('Failed to open documents database'));
    });
  }

  private waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
    });
  }

  private wrapRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
    });
  }
}
